import React, { useEffect, useRef, useState } from "react";

/**
 * The Convergence — a plain-English platform explainer.
 *
 * Two people (you & the other side) start apart and their paths curve toward
 * each other, meeting at a fair outcome in the centre. A short caption
 * cycles through the story in everyday language.
 */
const PHASES = [
  { k: "STEP 1", t: "Tell us what happened." },
  { k: "STEP 2", t: "Share your proof — photos, chats, bills." },
  { k: "STEP 3", t: "We match a trained mediator or arbitrator." },
  { k: "STEP 4", t: "Both sides talk it out, online." },
  { k: "STEP 5", t: "Agree on a fix — or get a fair ruling." },
  { k: "DONE",   t: "Move on. Peace, on paper." },
];

export default function ConvergenceMotif({ tone = "court" }) {
  const wrapRef = useRef(null);
  const stageRef = useRef(null);
  const [reduce, setReduce] = useState(false);
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const fn = () => setReduce(mq.matches);
    mq.addEventListener?.("change", fn);
    return () => mq.removeEventListener?.("change", fn);
  }, []);

  // Phase cycling
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setPhase((p) => (p + 1) % PHASES.length), 2200);
    return () => clearInterval(id);
  }, [reduce]);

  // Mouse parallax (subtle)
  useEffect(() => {
    if (reduce) return;
    const wrap = wrapRef.current; const stage = stageRef.current;
    if (!wrap || !stage) return;
    let raf = 0; let tx = 0, ty = 0;
    const onMove = (e) => {
      const r = wrap.getBoundingClientRect();
      tx = ((e.clientX - r.left) / r.width - 0.5) * 12;
      ty = ((e.clientY - r.top)  / r.height - 0.5) * -8;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const apply = () => {
      raf = 0;
      stage.style.setProperty("--mx", `${tx}deg`);
      stage.style.setProperty("--my", `${ty}deg`);
    };
    const onLeave = () => { stage.style.setProperty("--mx", "0deg"); stage.style.setProperty("--my", "0deg"); };
    wrap.addEventListener("mousemove", onMove);
    wrap.addEventListener("mouseleave", onLeave);
    return () => { wrap.removeEventListener("mousemove", onMove); wrap.removeEventListener("mouseleave", onLeave); if (raf) cancelAnimationFrame(raf); };
  }, [reduce]);

  // Palette per tone
  const isDark = tone === "court";
  const lineMain  = isDark ? "#EDE6D8" : "#1F3A34";
  const lineFaint = isDark ? "rgba(237,230,216,0.30)" : "rgba(31,58,52,0.30)";
  const gridFaint = isDark ? "rgba(237,230,216,0.08)" : "rgba(31,58,52,0.08)";
  const labelDim  = isDark ? "rgba(237,230,216,0.55)" : "rgba(31,58,52,0.55)";
  const clay = "#C2542A";

  // SVG path "d" attributes — two long curves that CROSS at the centre.
  // Path 1: Claimant journey (top-left → centre → bottom-right)
  // Path 2: Respondent journey (top-right → centre → bottom-left)
  // The dots meet at the centre = RESOLVED.
  const claimantPath  = "M 60 80  C 200 80, 200 320, 440 320";
  const respondentPath = "M 440 80 C 300 80, 300 320, 60 320";

  return (
    <div
      ref={wrapRef}
      className="relative w-full aspect-[5/4] select-none"
      style={{ perspective: "1300px" }}
      aria-hidden="true"
      data-testid="convergence-motif"
    >
      <div
        ref={stageRef}
        className="absolute inset-0"
        style={{
          transformStyle: "preserve-3d",
          transform: reduce
            ? "rotateX(8deg) rotateY(-6deg)"
            : "rotateX(calc(8deg + var(--my, 0deg))) rotateY(calc(-6deg + var(--mx, 0deg)))",
          animation: reduce ? "none" : "esulah-stage-osc 14s ease-in-out infinite",
          willChange: "transform",
          transition: "transform .35s ease-out",
        }}
      >
        {/* Far depth grid plane — sits behind */}
        <svg viewBox="0 0 500 400" className="absolute inset-0 w-full h-full" style={{ transform: "translateZ(-60px)" }}>
          {Array.from({ length: 9 }).map((_, i) => (
            <line key={`h${i}`} x1="0" x2="500" y1={50 + i * 40} y2={50 + i * 40} stroke={gridFaint} strokeWidth="0.5" />
          ))}
          {Array.from({ length: 11 }).map((_, i) => (
            <line key={`v${i}`} y1="0" y2="400" x1={50 + i * 40} x2={50 + i * 40} stroke={gridFaint} strokeWidth="0.5" />
          ))}
        </svg>

        {/* Mid depth — faint mirror of the convergence lines */}
        <svg viewBox="0 0 500 400" className="absolute inset-0 w-full h-full" style={{ transform: "translateZ(-25px)" }}>
          <path d={claimantPath} fill="none" stroke={lineFaint} strokeWidth="1" />
          <path d={respondentPath} fill="none" stroke={lineFaint} strokeWidth="1" />
        </svg>

        {/* Foreground SVG — main lines + animated tracers + corner anchors */}
        <svg viewBox="0 0 500 400" className="absolute inset-0 w-full h-full" style={{ transform: "translateZ(0)" }}>
          <defs>
            <linearGradient id="tracerGlow" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor={clay} stopOpacity="0" />
              <stop offset="1" stopColor={clay} stopOpacity="0.8" />
            </linearGradient>
            <radialGradient id="dotGlow">
              <stop offset="0" stopColor={clay} stopOpacity="0.9" />
              <stop offset="1" stopColor={clay} stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Plain-English anchors — two people starting apart */}
          <text x="50" y="60" fill={labelDim} fontFamily="Space Mono, monospace" fontSize="10" letterSpacing="2">YOU</text>
          <text x="450" y="60" fill={labelDim} fontFamily="Space Mono, monospace" fontSize="10" letterSpacing="2" textAnchor="end">THE OTHER SIDE</text>
          <text x="76" y="74" fill={lineMain} fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="1.5" opacity="0.75">Your story</text>
          <text x="424" y="74" fill={lineMain} fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="1.5" opacity="0.75" textAnchor="end">Their reply</text>
          <text x="76" y="356" fill={lineMain} fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="1.5" opacity="0.75">Talk online</text>
          <text x="424" y="356" fill={lineMain} fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="1.5" opacity="0.75" textAnchor="end">Talk online</text>

          {/* Two crossing journey curves */}
          <path d={claimantPath}   id="cm-path-claim" fill="none" stroke={lineMain} strokeWidth="1.4" strokeLinecap="round" />
          <path d={respondentPath} id="cm-path-resp"  fill="none" stroke={lineMain} strokeWidth="1.4" strokeLinecap="round" />

          {/* Centre meeting marker */}
          <line x1="218" y1="200" x2="282" y2="200" stroke={clay} strokeWidth="2" strokeLinecap="square" />
          <circle cx="250" cy="200" r="14" fill={clay} opacity="0.10">
            {!reduce && <animate attributeName="r" values="14;26;14" dur="3.4s" repeatCount="indefinite" />}
            {!reduce && <animate attributeName="opacity" values="0.12;0;0.12" dur="3.4s" repeatCount="indefinite" />}
          </circle>
          <circle cx="250" cy="200" r="4.5" fill={clay}>
            {!reduce && <animate attributeName="r" values="4.5;5.8;4.5" dur="3.4s" repeatCount="indefinite" />}
          </circle>
          <text x="250" y="234" fill={clay} fontFamily="Space Mono, monospace" fontSize="11" letterSpacing="2.5" textAnchor="middle">A FAIR OUTCOME</text>

          {/* Four corner anchors */}
          {[[60,80],[440,80],[60,320],[440,320]].map(([x,y], i) => (
            <g key={i}>
              <circle cx={x} cy={y} r="3" fill={lineMain} />
              <circle cx={x} cy={y} r="9" fill="none" stroke={lineMain} strokeOpacity="0.25" strokeWidth="0.8">
                {!reduce && <animate attributeName="r" values="6;14;6" dur="3.6s" begin={`${i * 0.4}s`} repeatCount="indefinite" />}
                {!reduce && <animate attributeName="stroke-opacity" values="0.35;0;0.35" dur="3.6s" begin={`${i * 0.4}s`} repeatCount="indefinite" />}
              </circle>
            </g>
          ))}

          {/* Two journey tracers that meet at centre at the midpoint of each cycle */}
          {!reduce && (
            <>
              {/* Claimant dot */}
              <g>
                <circle r="18" fill="url(#dotGlow)" opacity="0.55">
                  <animateMotion dur="5.4s" repeatCount="indefinite" rotate="auto" begin="0s">
                    <mpath href="#cm-path-claim" />
                  </animateMotion>
                </circle>
                <circle r="3.4" fill={clay}>
                  <animateMotion dur="5.4s" repeatCount="indefinite" rotate="auto" begin="0s">
                    <mpath href="#cm-path-claim" />
                  </animateMotion>
                </circle>
              </g>
              {/* Respondent dot — exactly in phase so they cross at centre */}
              <g>
                <circle r="18" fill="url(#dotGlow)" opacity="0.55">
                  <animateMotion dur="5.4s" repeatCount="indefinite" rotate="auto" begin="0s">
                    <mpath href="#cm-path-resp" />
                  </animateMotion>
                </circle>
                <circle r="3.4" fill={clay}>
                  <animateMotion dur="5.4s" repeatCount="indefinite" rotate="auto" begin="0s">
                    <mpath href="#cm-path-resp" />
                  </animateMotion>
                </circle>
              </g>
            </>
          )}
        </svg>

        {/* Layered phase caption — sits in front, 3D-offset */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none text-center"
          style={{ transform: "translate(-50%, -50%) translateZ(40px)" }}
        >
          <div className="px-3 py-2 bg-ink/60 backdrop-blur-sm" style={{ borderRadius: 3 }}>
            <div
              key={phase}
              className="font-mono text-[10px] tracking-[0.22em] text-clay"
              style={{ animation: reduce ? "none" : "esulah-phase-in 0.5s cubic-bezier(.2,.7,.3,1) both" }}
            >
              {PHASES[phase].k}
            </div>
            <div
              key={`t-${phase}`}
              className="font-display text-[13px] text-sand mt-1 max-w-[200px]"
              style={{ fontWeight: 400, animation: reduce ? "none" : "esulah-phase-in 0.5s 0.05s cubic-bezier(.2,.7,.3,1) both" }}
            >
              {PHASES[phase].t}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes esulah-stage-osc {
          0%, 100% { transform: rotateX(8deg) rotateY(-6deg); }
          50%      { transform: rotateX(6deg) rotateY( 4deg); }
        }
        @keyframes esulah-phase-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
